// components/ScannerTriggerBuilder.jsx
//
// Single-expression trigger logic builder for scanner profiles.
// Reuses ConditionsPanel + IndicatorPicker from conditionBuilder.jsx
// (requires those to be exported — see FRONTEND_CHANGES.md section 1).
//
// Trigger types:
//   - expression          : standard indicator-based logic (most common)
//   - composite           : AND/OR over sub-conditions (v2)
//   - volume_spike        : custom evaluator with window/baseline/multiplier
//   - liquidation_cascade : custom evaluator on forceOrder buffer (futures only)

import React, { useState, useMemo, useCallback } from 'react';
import {
  Box, Typography, Paper, Slider, TextField, InputAdornment, Chip,
  FormControl, Select, MenuItem, Tooltip,
} from '@mui/material';
import { FlashOn, ShowChart, BarChart, Bolt, Schedule } from '@mui/icons-material';

// Imports from conditionBuilder — see FRONTEND_CHANGES.md for the export list
import {
  ConditionsPanel,
  CANDLE_INDICATORS,
  FUTURES_INDICATORS,
  TICK_INDICATORS,
  buildPreview,
} from './conditionBuilder';

/* ═══════════════════════════════════════════════════════════════════════════════
   THEME — matched to StrategyBuilder
   ═══════════════════════════════════════════════════════════════════════════════ */

const C = {
  bg0: '#0a0a0a', bg1: '#111111', bg2: '#161616', bg3: '#1a1a1a', bg4: '#222222',
  b0: '#2a2a2a', b1: '#2a2a2a', b2: '#3a3a3a',
  t1: '#f5f5f5', t2: '#e0e0e0', t3: '#9a9a9a', t4: '#6a6a6a',
  purple: '#a78bfa', green: '#34d399', red: '#f87171',
  blue: '#38bdf8', yellow: '#fbbf24', orange: '#fb923c',
  longDim: 'rgba(52,211,153,0.08)', longBdr: 'rgba(52,211,153,0.25)',
  shortDim: 'rgba(248,113,113,0.08)', shortBdr: 'rgba(248,113,113,0.25)',
  futuresWarn: 'rgba(251,191,36,0.08)', futuresWarnBdr: 'rgba(251,191,36,0.25)',
  scannerDim: 'rgba(56,189,248,0.08)', scannerBdr: 'rgba(56,189,248,0.25)',
  mono: 'Roboto, "Helvetica Neue", Arial, sans-serif',
};

const panelSx = {
  bgcolor: C.bg3, border: '1px solid', borderColor: C.b0, borderRadius: 2, overflow: 'hidden',
};

const headerBarSx = {
  display: 'flex', alignItems: 'center',
  px: 2, py: 1, bgcolor: C.bg1,
  borderBottom: '1px solid', borderColor: C.b0, minHeight: 40,
};

const microLabel = {
  fontFamily: C.mono, fontSize: 10, fontWeight: 600, color: C.t4,
  textTransform: 'uppercase', letterSpacing: '0.08em',
  lineHeight: 1, mb: '5px', display: 'block',
};

const fieldSx = {
  bgcolor: C.bg1, borderRadius: 1.5,
  '& fieldset': { borderColor: C.b0, borderRadius: 1.5 },
  '&:hover fieldset': { borderColor: `${C.b2} !important` },
  '&.Mui-focused fieldset': { borderColor: `${C.b2} !important`, borderWidth: '1px !important' },
};


/* ═══════════════════════════════════════════════════════════════════════════════
   TRIGGER TYPES
   ═══════════════════════════════════════════════════════════════════════════════ */

const TRIGGER_TYPES = [
  {
    value: 'expression',
    label: 'Expression',
    icon: ShowChart,
    desc: 'Standard indicator logic (RSI, MACD, etc.) — same syntax as bot rules',
  },
  {
    value: 'volume_spike',
    label: 'Volume Spike',
    icon: BarChart,
    desc: 'Recent volume rate vs baseline — fires when activity surges',
  },
];


/* ═══════════════════════════════════════════════════════════════════════════════
   PRESET TRIGGERS
   ═══════════════════════════════════════════════════════════════════════════════ */

const SCANNER_PRESETS = [
  {
    name: 'Momentum Scalper',
    desc: 'RSI bullish + price above VWAP — 5m chart',
    type: 'expression',
    interval: '5m',
    conditions: [
      { ind: 'RSI', params: { period: 14 }, op: '>', val: '50' },
      { ind: 'Price', op: '>', cmp: 'VWAP', combinator: 'AND' },
    ],
    cooldown: 600,
  },
  {
    name: 'Capitulation Bounce',
    desc: 'Oversold RSI + price below lower BB — 5m',
    type: 'expression',
    interval: '5m',
    conditions: [
      { ind: 'RSI', params: { period: 14 }, op: '<', val: '25' },
      { ind: 'Price', op: '<=', cmp: 'BB_Lower', cmpParams: { period: 20, stdDev: 2 }, combinator: 'AND' },
    ],
    cooldown: 1800,
  },
  {
    name: 'Range Breakout',
    desc: 'Price breaks recent high with volume',
    type: 'expression',
    interval: '5m',
    conditions: [
      { ind: 'Price', op: '>', cmp: 'BB_Upper', cmpParams: { period: 20, stdDev: 2 } },
      { ind: 'Volume', op: '>', cmp: 'SMA', cmpParams: { period: 20 }, combinator: 'AND' },
    ],
    cooldown: 900,
  },
  {
    name: 'Vol Spike (60s/30m)',
    desc: '3× recent volume vs 30m baseline',
    type: 'volume_spike',
    config: { window_seconds: 60, baseline_seconds: 1800, multiplier: 3, min_recent_volume: 0 },
    cooldown: 600,
  },
  {
    name: 'Liq Cascade (long)',
    desc: '5+ long liquidations in 60s, $500k+ notional',
    type: 'liquidation_cascade',
    config: { window_seconds: 60, min_event_count: 5, min_notional_usd: 500_000, side: 'long_liquidations' },
    cooldown: 1200,
  },
];


/* ═══════════════════════════════════════════════════════════════════════════════
   PILL TOGGLE — same as FuturesComponents
   ═══════════════════════════════════════════════════════════════════════════════ */

function PillToggle({ value, options, onChange, accent }) {
  return (
    <Box sx={{ display: 'flex', gap: 0.5, p: 0.5, bgcolor: C.bg1, borderRadius: 1.5, border: '1px solid', borderColor: C.b0 }}>
      {options.map(opt => {
        const active = value === opt.value;
        return (
          <Tooltip key={opt.value} title={opt.desc || ''} arrow placement="top">
            <Box
              onClick={() => onChange(opt.value)}
              sx={{
                flex: 1, textAlign: 'center', px: 1.5, py: '5px',
                borderRadius: 1, cursor: 'pointer', transition: 'all 0.15s',
                bgcolor: active ? C.bg4 : 'transparent',
                border: '1px solid',
                borderColor: active ? (accent || C.b2) : 'transparent',
                '&:hover': { bgcolor: C.bg4, borderColor: C.b2 },
              }}
            >
              <Typography sx={{
                fontFamily: C.mono, fontSize: 11, fontWeight: active ? 700 : 500,
                color: active ? (accent || C.t1) : C.t4,
                letterSpacing: '0.03em', lineHeight: 1,
              }}>
                {opt.label}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════════
   VOLUME SPIKE PANEL
   ═══════════════════════════════════════════════════════════════════════════════ */

function VolumeSpikePanel({ config, onChange }) {
  const cfg = {
    window_seconds: 60,
    baseline_seconds: 1800,
    multiplier: 3,
    min_recent_volume: 0,
    ...config,
  };

  const patch = (k, v) => onChange({ ...cfg, [k]: v });

  return (
    <Paper elevation={0} sx={panelSx}>
      <Box sx={headerBarSx}>
        <Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: C.blue, mr: 1, opacity: 0.7 }} />
        <Typography sx={{ fontFamily: C.mono, fontSize: 12, fontWeight: 700, color: C.t1, letterSpacing: '0.05em' }}>
          VOLUME SPIKE CONFIG
        </Typography>
      </Box>

      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <Typography sx={microLabel}>RECENT WINDOW</Typography>
            <TextField
              size="small" type="number" fullWidth
              value={cfg.window_seconds}
              onChange={e => patch('window_seconds', Math.max(5, Number(e.target.value) || 60))}
              inputProps={{ step: 10, min: 5, style: { fontFamily: C.mono, fontSize: 13, padding: '7px 12px' } }}
              InputProps={{
                endAdornment: <InputAdornment position="end"><Typography sx={{ fontFamily: C.mono, fontSize: 11, color: C.t4 }}>sec</Typography></InputAdornment>,
                sx: { ...fieldSx, height: 38 },
              }}
            />
          </Box>
          <Box>
            <Typography sx={microLabel}>BASELINE WINDOW</Typography>
            <TextField
              size="small" type="number" fullWidth
              value={cfg.baseline_seconds}
              onChange={e => patch('baseline_seconds', Math.max(60, Number(e.target.value) || 1800))}
              inputProps={{ step: 60, min: 60, style: { fontFamily: C.mono, fontSize: 13, padding: '7px 12px' } }}
              InputProps={{
                endAdornment: <InputAdornment position="end"><Typography sx={{ fontFamily: C.mono, fontSize: 11, color: C.t4 }}>sec</Typography></InputAdornment>,
                sx: { ...fieldSx, height: 38 },
              }}
            />
          </Box>
        </Box>

        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={microLabel}>MULTIPLIER</Typography>
            <Typography sx={{ fontFamily: C.mono, fontSize: 16, fontWeight: 700, color: C.blue }}>{cfg.multiplier}×</Typography>
          </Box>
          <Slider
            value={cfg.multiplier}
            onChange={(_, v) => patch('multiplier', v)}
            min={1.5} max={20} step={0.5}
            sx={{
              color: C.blue,
              '& .MuiSlider-thumb': { width: 14, height: 14, borderRadius: 1.5, boxShadow: 'none' },
              '& .MuiSlider-track': { height: 3, borderRadius: 0 },
              '& .MuiSlider-rail': { height: 3, bgcolor: C.b0, opacity: 1 },
            }}
          />
          <Typography sx={{ fontFamily: C.mono, fontSize: 10, color: C.t4, mt: 0.5 }}>
            Fire when last {cfg.window_seconds}s volume rate is ≥{cfg.multiplier}× the prior {cfg.baseline_seconds}s baseline rate
          </Typography>
        </Box>

        <Box>
          <Typography sx={microLabel}>MIN RECENT VOLUME (OPTIONAL)</Typography>
          <TextField
            size="small" type="number" fullWidth
            value={cfg.min_recent_volume}
            onChange={e => patch('min_recent_volume', Math.max(0, Number(e.target.value) || 0))}
            placeholder="0"
            inputProps={{ step: 1000, min: 0, style: { fontFamily: C.mono, fontSize: 13, padding: '7px 12px' } }}
            sx={{ '& .MuiInputBase-root': { ...fieldSx, height: 38 } }}
          />

        </Box>
      </Box>
    </Paper>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════════
   LIQUIDATION CASCADE PANEL
   ═══════════════════════════════════════════════════════════════════════════════ */

function LiquidationCascadePanel({ config, onChange }) {
  const cfg = {
    window_seconds: 60,
    min_event_count: 5,
    min_notional_usd: 500_000,
    side: 'long_liquidations',
    ...config,
  };

  const patch = (k, v) => onChange({ ...cfg, [k]: v });

  return (
    <Paper elevation={0} sx={panelSx}>
      <Box sx={headerBarSx}>
        <Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: C.orange, mr: 1, opacity: 0.7 }} />
        <Typography sx={{ fontFamily: C.mono, fontSize: 12, fontWeight: 700, color: C.t1, letterSpacing: '0.05em' }}>
          LIQUIDATION CASCADE CONFIG
        </Typography>
        <Chip
          size="small" label="FUTURES"
          sx={{ ml: 1.5, height: 18, fontFamily: C.mono, fontSize: 9, fontWeight: 700, bgcolor: C.futuresWarn, color: C.yellow, border: '1px solid', borderColor: C.futuresWarnBdr }}
        />
      </Box>

      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Box>
          <Typography sx={microLabel}>SIDE</Typography>
          <PillToggle
            value={cfg.side}
            options={[
              { value: 'long_liquidations', label: 'Long Liqs', desc: 'Long positions being force-closed (down move)' },
              { value: 'short_liquidations', label: 'Short Liqs', desc: 'Short positions being force-closed (up move)' },
              { value: 'either', label: 'Either', desc: 'Any liquidation direction' },
            ]}
            onChange={v => patch('side', v)}
            accent={C.orange}
          />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <Typography sx={microLabel}>WINDOW</Typography>
            <TextField
              size="small" type="number" fullWidth
              value={cfg.window_seconds}
              onChange={e => patch('window_seconds', Math.max(5, Number(e.target.value) || 60))}
              inputProps={{ step: 10, min: 5, style: { fontFamily: C.mono, fontSize: 13, padding: '7px 12px' } }}
              InputProps={{
                endAdornment: <InputAdornment position="end"><Typography sx={{ fontFamily: C.mono, fontSize: 11, color: C.t4 }}>sec</Typography></InputAdornment>,
                sx: { ...fieldSx, height: 38 },
              }}
            />
          </Box>
          <Box>
            <Typography sx={microLabel}>MIN EVENTS</Typography>
            <TextField
              size="small" type="number" fullWidth
              value={cfg.min_event_count}
              onChange={e => patch('min_event_count', Math.max(1, Number(e.target.value) || 5))}
              inputProps={{ step: 1, min: 1, style: { fontFamily: C.mono, fontSize: 13, padding: '7px 12px' } }}
              sx={{ '& .MuiInputBase-root': { ...fieldSx, height: 38 } }}
            />
          </Box>
        </Box>

        <Box>
          <Typography sx={microLabel}>MIN NOTIONAL (USD)</Typography>
          <TextField
            size="small" type="number" fullWidth
            value={cfg.min_notional_usd}
            onChange={e => patch('min_notional_usd', Math.max(0, Number(e.target.value) || 0))}
            inputProps={{ step: 10_000, min: 0, style: { fontFamily: C.mono, fontSize: 13, padding: '7px 12px' } }}
            InputProps={{
              endAdornment: <InputAdornment position="end"><Typography sx={{ fontFamily: C.mono, fontSize: 11, color: C.t4 }}>USD</Typography></InputAdornment>,
              sx: { ...fieldSx, height: 38 },
            }}
          />
          <Typography sx={{ fontFamily: C.mono, fontSize: 10, color: C.t4, mt: 0.5 }}>
            Sum of (price × qty) across qualifying events must exceed this
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════════
   COOLDOWN PANEL
   ═══════════════════════════════════════════════════════════════════════════════ */

function CooldownPanel({ value, onChange }) {
  const minutes = Math.floor(value / 60);
  return (
    <Paper elevation={0} sx={panelSx}>
      <Box sx={{ ...headerBarSx, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Schedule sx={{ fontSize: 14, color: C.t3 }} />
          <Typography sx={{ fontFamily: C.mono, fontSize: 12, fontWeight: 700, color: C.t1, letterSpacing: '0.05em' }}>
            COOLDOWN
          </Typography>
        </Box>
        <Typography sx={{ fontFamily: C.mono, fontSize: 12, color: C.t3 }}>
          {minutes >= 1 ? `${minutes} min` : `${value}s`} per symbol
        </Typography>
      </Box>

      <Box sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            size="small" type="number"
            value={value}
            onChange={e => onChange(Math.max(0, Number(e.target.value) || 0))}
            inputProps={{ step: 30, min: 0, style: { fontFamily: C.mono, fontSize: 14, fontWeight: 600, padding: '7px 12px', textAlign: 'right' } }}
            InputProps={{
              endAdornment: <InputAdornment position="end"><Typography sx={{ fontFamily: C.mono, fontSize: 11, color: C.t4 }}>sec</Typography></InputAdornment>,
              sx: { ...fieldSx, height: 38 },
            }}
            sx={{ width: 130 }}
          />
          <Slider
            value={value}
            onChange={(_, v) => onChange(v)}
            min={0} max={3600} step={30}
            sx={{
              flex: 1, color: C.t3,
              '& .MuiSlider-thumb': { width: 12, height: 12, borderRadius: 1, boxShadow: 'none' },
              '& .MuiSlider-track': { height: 3, borderRadius: 0 },
              '& .MuiSlider-rail': { height: 3, bgcolor: C.b0, opacity: 1 },
            }}
          />
        </Box>
        <Typography sx={{ fontFamily: C.mono, fontSize: 10, color: C.t4, mt: 1 }}>
          After firing on a symbol, scanner won't fire again for that same symbol until cooldown expires.
        </Typography>
      </Box>
    </Paper>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════════
   PRESETS PANEL
   ═══════════════════════════════════════════════════════════════════════════════ */

function PresetsPanel({ marketType, onApply }) {
  const presets = useMemo(
    () => SCANNER_PRESETS.filter(p => p.type !== 'liquidation_cascade' || marketType === 'futures'),
    [marketType]
  );

  return (
    <Paper elevation={0} sx={panelSx}>
      <Box sx={headerBarSx}>
        <Typography sx={{ fontFamily: C.mono, fontSize: 12, fontWeight: 700, color: C.t1, letterSpacing: '0.05em' }}>
          SCANNER PRESETS
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
        {presets.map((p, i) => (
          <Box
            key={i}
            onClick={() => onApply(p)}
            sx={{
              flex: '1 1 200px', px: 2, py: 1.25, cursor: 'pointer',
              borderRight: i < presets.length - 1 ? '1px solid' : 'none',
              borderColor: C.b0,
              transition: 'background 0.15s',
              '&:hover': { bgcolor: C.bg4 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
              <Typography sx={{ fontFamily: C.mono, fontSize: 12, fontWeight: 600, color: C.t2 }}>{p.name}</Typography>
              <Chip
                size="small" label={p.type.replace('_', ' ')}
                sx={{
                  height: 16, fontFamily: C.mono, fontSize: 8.5, fontWeight: 700,
                  bgcolor: 'transparent', color: C.t4,
                  border: '1px solid', borderColor: C.b0,
                  '& .MuiChip-label': { px: 0.75, textTransform: 'uppercase', letterSpacing: '0.05em' },
                }}
              />
            </Box>
            <Typography sx={{ fontFamily: C.mono, fontSize: 11, color: C.t4, lineHeight: 1.4 }}>{p.desc}</Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════════
   MAIN EXPORT
   ═══════════════════════════════════════════════════════════════════════════════ */

const mkId = () => crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`;

const getDefaults = (id, reg) => {
  const m = reg[id];
  if (!m?.params?.length) return {};
  return m.params.reduce((a, p) => ({ ...a, [p.n]: p.d }), {});
};


export default function ScannerTriggerBuilder({
  trigger,                  // { type, conditions?|expression?, config?, ... }
  onTriggerChange,
  cooldownSeconds = 600,
  onCooldownChange,
  marketType = 'spot',
  interval = '5m',
  onIntervalChange,
}) {
  const triggerType = trigger?.type || 'expression';

  // Indicators set depends on market type / interval
  const INDICATORS = useMemo(() => {
    if (interval === 'tick') return TICK_INDICATORS;
    if (marketType === 'futures') return FUTURES_INDICATORS;
    return CANDLE_INDICATORS;
  }, [marketType, interval]);


  // ── Trigger type change — reset relevant fields ─────────────────────────────
  const handleTypeChange = useCallback((newType) => {
    if (newType === 'expression') {
      onTriggerChange({ type: 'expression', conditions: [], indicators: [] });
    } else if (newType === 'volume_spike') {
      onTriggerChange({
        type: 'volume_spike',
        config: { window_seconds: 60, baseline_seconds: 1800, multiplier: 3, min_recent_volume: 0 },
      });
    } else if (newType === 'liquidation_cascade') {
      onTriggerChange({
        type: 'liquidation_cascade',
        config: { window_seconds: 60, min_event_count: 5, min_notional_usd: 500_000, side: 'long_liquidations' },
      });
    }
  }, [onTriggerChange]);


  // ── Apply preset ────────────────────────────────────────────────────────────
  const applyPreset = useCallback((preset) => {
    if (preset.type === 'expression') {
      const conditions = (preset.conditions || []).map((c, i) => ({
        id: mkId(),
        indicator: c.ind,
        params: c.params || getDefaults(c.ind, INDICATORS),
        operator: c.op || '',
        value: c.val || '',
        compareIndicator: c.cmp || '',
        compareParams: c.cmpParams || (c.cmp ? getDefaults(c.cmp, INDICATORS) : {}),
        combinator: c.combinator || (i > 0 ? 'AND' : null),
      }));
      onTriggerChange({ type: 'expression', conditions });
    } else {
      onTriggerChange({ type: preset.type, config: preset.config });
    }
    if (preset.cooldown != null) onCooldownChange?.(preset.cooldown);
    if (preset.interval) onIntervalChange?.(preset.interval);
  }, [INDICATORS, onTriggerChange, onCooldownChange, onIntervalChange]);


  // ── Handle condition changes (expression mode) ──────────────────────────────
  const handleConditionsChange = useCallback((conditions) => {
    onTriggerChange({ ...trigger, type: 'expression', conditions });
  }, [trigger, onTriggerChange]);


  // ── Handle config changes (custom evaluator modes) ──────────────────────────
  const handleConfigChange = useCallback((config) => {
    onTriggerChange({ ...trigger, config });
  }, [trigger, onTriggerChange]);


  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

      {/* Trigger type selector + interval */}
      <Paper elevation={0} sx={panelSx}>
        <Box sx={headerBarSx}>
          <Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: C.blue, mr: 1, opacity: 0.8 }} />
          <Typography sx={{ fontFamily: C.mono, fontSize: 12, fontWeight: 700, color: C.t1, letterSpacing: '0.05em' }}>
            TRIGGER TYPE
          </Typography>
        </Box>

        <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
             {TRIGGER_TYPES.map(t => {
              const Icon = t.icon;
              const active = triggerType === t.value;
              const disabled = t.comingSoon || (t.value === 'liquidation_cascade' && marketType !== 'futures');
              const tooltipMsg = t.comingSoon
                ? 'Liquidation cascade trigger — coming soon'
                : (disabled ? 'Futures only' : '');

              return (
                <Tooltip key={t.value} title={tooltipMsg} arrow>
                  <Box
                    onClick={() => !disabled && handleTypeChange(t.value)}
                    sx={{
                      px: 1.5, py: 1.25,
                      border: '1px solid',
                      borderColor: active ? t.bdr : C.b0,
                      borderRadius: 1.5,
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.4 : 1,
                      bgcolor: active ? t.dimBg : 'transparent',
                      transition: 'all 0.15s',
                      '&:hover': disabled ? {} : { borderColor: t.bdr, bgcolor: t.dimBg },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                      <Icon sx={{ fontSize: 13, color: active ? t.color : C.t4 }} />
                      <Typography sx={{
                        fontFamily: C.mono, fontSize: 11, fontWeight: 700,
                        color: active ? t.color : C.t3,
                        letterSpacing: '0.02em',
                      }}>
                        {t.label}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontFamily: C.mono, fontSize: 9.5, color: C.t4, lineHeight: 1.4 }}>
                      {t.desc}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>

          {/* Interval selector — only relevant for expression mode */}
          {triggerType === 'expression' && (
            <Box>
              <Typography sx={microLabel}>INDICATOR INTERVAL</Typography>
              <FormControl size="small" fullWidth>
                <Select
                  value={interval}
                  onChange={e => onIntervalChange?.(e.target.value)}
                  sx={{
                    ...fieldSx, height: 38,
                    '& .MuiSelect-select': { fontFamily: C.mono, fontSize: 13, py: '7px !important' },
                  }}
                  MenuProps={{ PaperProps: { sx: { bgcolor: C.bg1, border: '1px solid', borderColor: C.b2 } } }}
                >
                  {['tick', '1m', '3m', '5m', '15m', '30m', '1h', '4h'].map(i => (
                    <MenuItem key={i} value={i} sx={{ fontFamily: C.mono, fontSize: 12 }}>{i}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Presets */}
      <PresetsPanel marketType={marketType} onApply={applyPreset} />

      {/* Type-specific config panel */}
      {triggerType === 'expression' && (
        <ConditionsPanel
          type="buy"
          conditions={trigger?.conditions || []}
          indicators={INDICATORS}
          onChange={handleConditionsChange}
          marketType={marketType}
        />
      )}

      {triggerType === 'volume_spike' && (
        <VolumeSpikePanel
          config={trigger?.config || {}}
          onChange={handleConfigChange}
        />
      )}

      {triggerType === 'liquidation_cascade' && (
        <LiquidationCascadePanel
          config={trigger?.config || {}}
          onChange={handleConfigChange}
        />
      )}

      {/* Cooldown */}
      <CooldownPanel value={cooldownSeconds} onChange={onCooldownChange || (() => { })} />
    </Box>
  );
}